import React from 'react';
import { StyleSheet, Text, View, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';
import { parseDocument } from 'htmlparser2';
import type { DataNode, Element, Node } from 'domhandler';
import { tagsStyles } from 'src/presentation/components/organisms/styles/HtmlStyles';
import HtmlElements from 'src/core/utils/HtmlElements';
import Colors from 'src/core/utils/Colors';
import Translations from 'src/core/utils/Translations';
import { useLeaveAppStore } from 'src/domain/stores/useLeaveAppStore';
import Dimens from 'src/core/utils/Dimens';
import { ElementType } from 'domelementtype';

type HtmlViewProps = {
	html: string;
	style?: StyleProp<ViewStyle>;
	// Overrides the body text of the rendered html, so a card can set its own size without a second parser.
	textStyle?: StyleProp<TextStyle>;
};

export const HtmlView: React.FC<HtmlViewProps> = ({ html, style, textStyle }) => {
	const dom = parseDocument(html).children;
	const { confirmLeaving } = useLeaveAppStore();

	const renderNode = (node: Node, index: number): React.ReactNode => {
		if (node.type === ElementType.Text) {
			return (
				<Text key={index} style={textStyle}>
					{(node as DataNode).data}
				</Text>
			);
		}

		if (node.type === ElementType.Tag) {
			const el = node as Element;
			const children = el.children?.map((child, i) => renderNode(child, i)) ?? [];

			switch (el.name) {
				case HtmlElements.p:
					return (
						<Text key={index} style={[styles.bodyText, tagsStyles.body, textStyle]}>
							{children}
						</Text>
					);
				case HtmlElements.i:
				case HtmlElements.em:
					return (
						<Text key={index} style={[styles.bodyText, tagsStyles.italic as TextStyle, textStyle]}>
							{children}
						</Text>
					);
				case HtmlElements.b:
				case HtmlElements.strong:
					return (
						<Text key={index} style={[styles.bodyText, tagsStyles.strong, textStyle]}>
							{children}
						</Text>
					);
				case HtmlElements.s:
				case HtmlElements.del:
					return (
						<Text key={index} style={[styles.bodyText, tagsStyles.del as TextStyle, textStyle]}>
							{children}
						</Text>
					);
				case HtmlElements.u:
					return (
						<Text key={index} style={[styles.bodyText, styles.underline, textStyle]}>
							{children}
						</Text>
					);
				case HtmlElements.h1:
					return (
						<Text key={index} style={[styles.headingOne, tagsStyles.h1]}>
							{children}
						</Text>
					);
				case HtmlElements.h2:
					return (
						<Text key={index} style={[styles.headingTwo, tagsStyles.h2]}>
							{children}
						</Text>
					);
				case HtmlElements.h3:
					return (
						<Text key={index} style={[styles.headingThree, tagsStyles.h3]}>
							{children}
						</Text>
					);
				case HtmlElements.br:
					return (
						<Text key={index} style={[styles.bodyText, textStyle]}>
							{'\n'}
						</Text>
					);
				case HtmlElements.li:
					return (
						<Text key={index} style={[styles.bodyText, tagsStyles.li, { marginBottom: Dimens.padding0 }, textStyle]}>
							{children}
						</Text>
					);
				case HtmlElements.ul:
				case HtmlElements.ol: {
					const ordered = el.name === HtmlElements.ol;
					const listItems = el.children.filter(
						(child) => child.type === ElementType.Tag && (child as Element).name === HtmlElements.li,
					);
					return (
						<View key={index} style={ordered ? tagsStyles.ol : tagsStyles.ul}>
							{listItems.map((li, idx) => (
								<View key={idx} style={styles.listRow}>
									<Text style={[tagsStyles.body, styles.listMarker, textStyle]}>{ordered ? `${idx + 1}.` : '•'}</Text>
									<Text style={[tagsStyles.body, styles.listContent, textStyle]}>
										{(li as Element).children?.map((child, i) => renderNode(child, i)) ?? []}
									</Text>
								</View>
							))}
						</View>
					);
				}
				case HtmlElements.a: {
					const href = el.attribs?.['href'] ?? '';
					return (
						<Text
							key={index}
							style={[tagsStyles.a as TextStyle, textStyle]}
							onPress={() => href && confirmLeaving({ url: href, description: Translations.leaveAppDescription })}
						>
							{children}
						</Text>
					);
				}
				case HtmlElements.span:
					if (el.attribs?.['style']?.includes('text-decoration: underline')) {
						return (
							<Text key={index} style={[styles.bodyText, styles.underline, textStyle]}>
								{children}
							</Text>
						);
					}
					return (
						<Text key={index} style={[styles.bodyText, textStyle]}>
							{children}
						</Text>
					);
				default:
					return (
						<Text key={index} style={textStyle}>
							{children}
						</Text>
					);
			}
		}

		return null;
	};

	const blockTags = new Set<string>([
		HtmlElements.p,
		HtmlElements.h1,
		HtmlElements.h2,
		HtmlElements.h3,
		HtmlElements.ul,
		HtmlElements.ol,
	]);

	const isBlock = (node: Node): boolean => node.type === ElementType.Tag && blockTags.has((node as Element).name);

	const renderInlineAware = (nodes: Node[]): React.ReactNode[] => {
		const output: React.ReactNode[] = [];
		let inlineRun: React.ReactNode[] = [];
		let runKey = 0;

		const flushInlineRun = () => {
			if (inlineRun.length === 0) return;
			output.push(
				<Text
					key={`inline-${runKey++}`}
					style={[styles.bodyText, tagsStyles.body, { marginBottom: Dimens.padding0 }, textStyle]}
				>
					{inlineRun}
				</Text>,
			);
			inlineRun = [];
		};

		nodes.forEach((node, index) => {
			if (isBlock(node)) {
				flushInlineRun();
				output.push(renderNode(node, index));
			} else {
				inlineRun.push(renderNode(node, index));
			}
		});

		flushInlineRun();
		return output;
	};

	return <View style={style}>{renderInlineAware(dom)}</View>;
};

const styles = StyleSheet.create({
	bodyText: {
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight18,
		color: Colors.gray800,
	},
	headingOne: {
		fontSize: Dimens.fontSize20,
		lineHeight: Dimens.lineHeight24,
		color: Colors.gray800,
	},
	headingTwo: {
		fontSize: Dimens.fontSize18,
		lineHeight: Dimens.lineHeight18,
		color: Colors.gray800,
	},
	headingThree: {
		fontSize: Dimens.fontSize18,
		lineHeight: Dimens.lineHeight24,
		color: Colors.gray800,
	},
	underline: {
		textDecorationLine: 'underline',
	},
	listRow: {
		flexDirection: 'row',
		marginBottom: Dimens.padding0,
	},
	listMarker: {
		marginBottom: Dimens.padding0,
	},
	listContent: {
		flex: Dimens.flex1,
		marginBottom: Dimens.padding0,
		paddingLeft: Dimens.padding8,
	},
});
